// packages/frontend/src/components/admin/SportSeasonsBulkView.tsx
import { useState, useRef } from 'react';
import { Upload, Download } from 'lucide-react';
import { sportSeasonDefaultsApi } from '../../lib/api';
import toast from 'react-hot-toast';

// Shape returned by sportSeasonDefaultsApi.importPreview — mirrors the backend's
// /import/preview response body: { willCreate, willUpdate, unchanged }. The
// inner row/change shapes are not used by this view (it only renders counts),
// so they're typed as `unknown` to keep the component honest without `any`.
interface ImportPreviewDiff {
  willCreate: unknown[];
  willUpdate: unknown[];
  unchanged: unknown[];
}

interface ImportApplyResult {
  created: number;
  updated: number;
  total: number;
}

function describeError(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const res = (err as { response?: { data?: { message?: string }; status?: number } }).response;
    if (res?.data?.message) return res.data.message;
    if (res?.status) return `Request failed: HTTP ${res.status}`;
  }
  return err instanceof Error ? err.message : 'Unknown error';
}

export function SportSeasonsBulkView() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ImportPreviewDiff | null>(null);
  const [csvText, setCsvText] = useState<string>('');
  const [busy, setBusy] = useState<null | 'previewing' | 'applying' | 'exporting'>(null);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('previewing');
    try {
      const text = await file.text();
      setCsvText(text);
      const diff = (await sportSeasonDefaultsApi.importPreview(text)) as ImportPreviewDiff;
      setPreview(diff);
    } catch (err) {
      toast.error(`Preview failed: ${describeError(err)}`);
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setBusy(null);
    }
  }

  async function onApply() {
    setBusy('applying');
    try {
      const result = (await sportSeasonDefaultsApi.importApply(csvText)) as ImportApplyResult;
      toast.success(`Created ${result.created}, updated ${result.updated} (total ${result.total})`);
      setPreview(null);
      setCsvText('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      toast.error(`Apply failed: ${describeError(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function onExport() {
    setBusy('exporting');
    try {
      const blob = await sportSeasonDefaultsApi.export();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sport-season-defaults.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`Export failed: ${describeError(err)}`);
    } finally {
      setBusy(null);
    }
  }

  const uploadDisabled = busy !== null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Bulk import / export
        </h3>
        <div className="flex items-center gap-2">
          <label
            className={`btn-secondary inline-flex items-center gap-2 ${
              uploadDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <Upload size={16} /> Upload CSV
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={onPickFile}
              disabled={uploadDisabled}
            />
          </label>
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-2"
            onClick={onExport}
            disabled={uploadDisabled}
          >
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {busy === 'previewing' && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Reading & previewing…</p>
      )}
      {busy === 'applying' && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Applying…</p>
      )}
      {busy === 'exporting' && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Exporting…</p>
      )}

      {preview && (
        <div className="card p-4">
          <p className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">Preview</p>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Will create
              </dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">
                {preview.willCreate.length}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Will update
              </dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">
                {preview.willUpdate.length}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Unchanged
              </dt>
              <dd className="font-semibold text-gray-900 dark:text-gray-100">
                {preview.unchanged.length}
              </dd>
            </div>
          </dl>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPreview(null)}
              disabled={busy !== null}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onApply}
              disabled={busy !== null}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
